// What this person can do to this andon, right now, in the order it needs it.
// Lifted from the Andon app's `lib/actions.ts`.
//
// ROLE IS A CAPABILITY, NOT A SURFACE.
//
// This is the function that makes that true. Everyone gets the same thread;
// what changes is the row of things you can say, composed from what THIS
// session is permitted to do and what THIS andon is waiting for. A PIN session
// gets the raiser's verbs; a personal account gets those plus the responder's.
//
// The rule the list obeys, in both directions:
//
//   nothing is hidden that this person could have done
//   nothing appears that would fail
//
// The second half is not cosmetic. Taking an andon writes an Odoo USER onto the
// record, and a PIN session is authenticated as the service account — its uid
// says nothing about the operator behind the PIN, so the server refuses,
// correctly, and a button that produces a refusal is worse than no button. The
// first half is why an operator who is also a responder does not have to sign
// out of one thing to do the other.
//
// AND THEY ARE THINGS YOU SAY.
//
// Every one of these posts into the thread as well as changing the state, so
// the escalation everybody sees has a visible cause. That is why they are a
// quick-reply row above the composer rather than an action bar.
//
// WHAT THE LIFT CHANGED. The labels are TR keys rather than strings, and
// `cancel` is a verb in the row: the frame draws "Sorted myself" beside the
// raiser's other verbs, where the Andon app kept the cancel behind Detail.
//
// AND WHAT FRAME v2.12 TOOK OUT (script L918, the `verbs` list). "I'm on it"
// is not in the frame or the spec: taking an andon lands in Responding, and
// a second verb to say "now I am really on it" was a step nobody on the floor
// made. Its fix-estimate sheet went with it; "+15 min" stays, because
// revising an estimate is meant to be cheaper than running out of one.

import type { AndonAlertVM } from '@/api/andon/types';
import type { StringKey, Translations } from '@/i18n/strings';

export type ActionKey =
  /** The line has stopped now. Only ever upward, only the person who raised it. */
  | 'stopped'
  /** The moment they predicted has arrived. Nobody else can see this. */
  | 'confirmStop'
  /** Give, or push out, the stop estimate. */
  | 'estimateStop'
  /**
   * Take it on, with an ETA. There is no claim without a time — and it is
   * offered on an andon somebody else already holds, because an andon that
   * cannot change hands is one that gets stuck on whoever answered first.
   */
  | 'ack'
  /** Fifteen more minutes on the fix. Revising is meant to be cheaper than running out. */
  | 'fixEta'
  /**
   * THE CLOSE. What you did and what caused it, in one act — the only way an
   * andon is done.
   */
  | 'close'
  | 'handover'
  | 'escalate'
  /**
   * Withdraw it, with a reason. The raiser's own: "sorted it myself" is the
   * commonest ending for an andon nobody needed to come to, and until it had a
   * verb the only way to end one was to wait for a responder to close it.
   */
  | 'cancel';

export interface Capability {
  /** A personal Odoo account. Taking, owning and closing need one. */
  mode: 'user' | 'kiosk' | 'none';
  /**
   * The server has marked the account as a leader. The frame's `isLeader`:
   * a leader may close any andon on the floor, not only the one they hold
   * (script L918, `canAct = isLeader || owner === me`).
   */
  leader: boolean;
}

/**
 * The row, ordered by what the andon is waiting for.
 *
 * "Waiting for" rather than "importance in the abstract": when the stop
 * forecast comes due, the row leads with confirming it, because at that moment
 * it is the only question in the building that this person can settle. Ten
 * minutes earlier the same andon leads with something else.
 */
export function actionsFor(alert: AndonAlertVM, cap: Capability): ActionKey[] {
  const out: ActionKey[] = [];
  if (!alert.open) return out;

  const raiser = alert.raisedByMe;
  const user = cap.mode === 'user';
  const owner = user && alert.mine;

  // ── what only the person who raised it can answer ──────────────────────
  if (raiser) {
    // ONE CHIP FOR THE STOP QUESTION, NOT THREE.
    //
    // `confirmStop` and `estimateStop` open the SAME SHEET. That sheet already
    // carries both answers — a big "It has stopped", and under it "Still
    // running? How much longer?" with the minute chips — so a second chip
    // beside the first would open the identical panel under a different name.
    // `stopped` would make it three, because its whole act is the sheet's
    // primary button.
    //
    // So the row is contextual: when the forecast comes due it leads with "Did
    // it stop?" and nothing else about stopping, because every answer lives
    // behind that one tap.
    if (alert.stopForecast?.due) {
      out.push('confirmStop');
    } else {
      // No forecast due: these two are genuinely different acts. "Stopped now"
      // declares the line is down THIS MOMENT and raises the star; "When will
      // it stop?" is a prediction about later.
      if (alert.stars < 3) out.push('stopped');
      if (alert.needsStopEstimate || alert.stopForecast) out.push('estimateStop');
    }
  }

  // ── what an Odoo account can do about it ───────────────────────────────
  if (user) {
    // TAKE IT ON, WHETHER OR NOT SOMEBODY ALREADY HAS.
    //
    // An andon one responder had picked up and not progressed could once not
    // be taken by anybody else, and could not be handed to them either,
    // because handing over was the owner's move alone. The only way out was an
    // Andon manager editing the record, which is not a thing that happens
    // while a line is stopped.
    //
    // Single ownership is untouched. There is still exactly one owner, set
    // under a row lock in Odoo; what changed is that the transfer is not
    // refused, and every takeover posts who took it from whom into the thread
    // where the previous owner is still a follower.
    if (!owner) out.push('ack');
    /**
     * SORTED: THE OWNER'S, OR A LEADER'S (frame script L918, `canAct`).
     *
     * The close is signed — it says who sorted it — so it belongs to the
     * person who holds the andon. A leader may close any andon on their
     * floor as well, because a leader walking past a line that is running
     * again should not have to take an andon off whoever fixed it in order
     * to record that it is fixed. Anybody else closing somebody's andon is a
     * takeover, and taking over is a deliberate act with its own button.
     *
     * It is offered from any open stage, not only Responding: the andon this
     * app is asked for most often after a shift is one raised about a
     * stoppage that is already over, and made to acknowledge it first the
     * person invents an ETA for a wait nobody did. The server fills in what
     * was skipped without fabricating a duration.
     *
     * "+15 min" rides with the close for the same people, and only once a fix
     * estimate exists to push out.
     */
    if (owner || cap.leader) {
      out.push('close');
      if (alert.responding) out.push('fixEta');
    }
    /**
     * ONLY ONCE SOMEBODY HAS TAKEN IT.
     *
     * Redirecting is open to any responder rather than the owner alone — the
     * person best placed to see an andon has gone to the wrong desk is rarely
     * the person it went to. What is refused is handing on an andon NOBODY HAS
     * TAKEN: the owner changed, no clock moved, the card stayed in New still
     * climbing the ladder, and the person it landed on inherited a response
     * time they had never been asked to agree to.
     *
     * So the row asks for the two acts in the order that keeps them honest —
     * take it, which stops the operator's clock on a time somebody actually
     * chose, then hand it to whoever should have it. The take sheet does both
     * in one gesture for the case this matters most in; see `EtaSheet`.
     *
     * The server refuses the same thing.
     */
    if (alert.stage !== 'new') out.push('handover');
    // ESCALATE IS ANYBODY'S WITH A CREDENTIALS SESSION (frame script L918:
    // the verb is in the row unconditionally). The person best placed to say
    // "this needs somebody senior" is often the one who has just realised
    // they are not that person — and often the one the ladder never named.
    // It posts who escalated into the thread, so it is never anonymous.
    out.push('escalate');
  }

  // ── the way out, for whoever called it in ──────────────────────────────
  //
  // The raiser's, and only the raiser's, in the row: it is a claim about the
  // problem — "there was nothing here to answer" — and the person who called
  // it in is the one placed to make it. The owner can still withdraw from
  // Detail; the server checks both under a row lock.
  if (raiser) out.push('cancel');

  return out;
}

/** The label, and how loud it is. One place, so every surface agrees. */
export const ACTION_LABEL: Record<ActionKey, { label: StringKey; tone: 'danger' | 'pos' | 'quiet' }> = {
  stopped: { label: 'verbStoppedNowT', tone: 'danger' },
  confirmStop: { label: 'verbDidItStopT', tone: 'danger' },
  // NOT "Estimate". `fixEta` below is also an estimate, and both can be in the
  // row at once — a person who raised an andon at their own station, owns it,
  // and is working on it gets every one of these. Two identical quiet chips
  // side by side is a coin flip about which question you are answering: this
  // one is the OPERATOR predicting when the line goes down, that one is the
  // RESPONDER pushing the fix out.
  estimateStop: { label: 'verbWhenWillItStopT', tone: 'quiet' },
  // Worded for the unowned case; `ackLabel` below picks the honest one.
  ack: { label: 'verbTakeItT', tone: 'pos' },
  fixEta: { label: 'verbPlusFifteenT', tone: 'quiet' },
  // The word the floor already used for the decision. It opens the close
  // sheet — what was done and what caused it — because that is the whole of
  // what "sorted" means now.
  close: { label: 'verbSortedT', tone: 'pos' },
  handover: { label: 'verbHandToSomeoneT', tone: 'quiet' },
  escalate: { label: 'verbEscalateT', tone: 'danger' },
  cancel: { label: 'verbSortedMyselfT', tone: 'quiet' },
};

/**
 * The VERB for taking an andon on, and only the verb.
 *
 * "Take it" and "Take over from Ben" are different promises, and a control
 * that says the first while doing the second is how somebody relieves a
 * colleague without realising they have.
 *
 * It stops at the verb because its callers finish the sentence differently —
 * a button says "— when will you be there?", a chip says nothing more — and a
 * label carrying half of one of those produced a run-on on the one screen that
 * has both.
 */
export function ackLabel(alert: Pick<AndonAlertVM, 'owner'>, t: Pick<Translations, 'verbTakeItT' | 'verbTakeOverFromT' | 'themT'>): string {
  if (alert.owner.unowned) return t.verbTakeItT;
  return `${t.verbTakeOverFromT} ${alert.owner.name?.split(' ')[0] ?? t.themT}`;
}

/**
 * How many minutes "+15" asks for, counted from now the way the server counts
 * a fix estimate.
 *
 * The revise call takes a fresh figure, not an increment, so "fifteen more"
 * has to be worked out here: whatever is left on the current estimate, plus
 * fifteen. A fix already running over has nothing left, so it becomes fifteen
 * from now — which is exactly what the responder means when they tap it. The
 * cap is the server's own week.
 */
export function plusFifteen(fixEta: { msLeft: number } | null): number {
  const left = fixEta ? Math.max(0, Math.ceil(fixEta.msLeft / 60_000)) : 0;
  return Math.min(10_080, left + 15);
}
